/**
 * NssSectorBenchmark — HESA NSS 2024/25 benchmark line on UK uni pages.
 *
 * When the uni has `satisfaction_pct_verified` (from the HESA NSS 2025
 * per-provider workbook), we render a "VERIFIED" badge + the per-uni
 * positivity + delta vs the UK sector benchmark. Otherwise we fall
 * back to the sector-benchmark-only line.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

export default function NssSectorBenchmark({ satisfactionScore, verifiedPct, nResponses }) {
  const [nss, setNss] = useState(null);
  useEffect(() => {
    api.get("/methodology/nss-2024")
       .then((r) => setNss(r.data.uk_all_subjects))
       .catch(() => setNss(null));
  }, []);

  if (!nss || satisfactionScore == null) return null;

  // If we have a per-provider verified % from HESA NSS 2025, use that.
  // Otherwise, back-solve from the 5-scale seeded score as a rough
  // visual proxy.
  const isVerified = verifiedPct != null;
  const uniPct = isVerified ? verifiedPct : Math.round((satisfactionScore / 5) * 100);
  const delta = Math.round(uniPct - nss.positivity_pct);
  const deltaSign = delta >= 0 ? "+" : "";

  return (
    <div
      className="mt-2 text-[11px] font-mono text-stone-600 leading-tight flex items-start gap-1.5"
      data-testid="nss-sector-benchmark"
      title={`Source: ${nss.source}. HESA NSS Q26 across ${nss.n_responses.toLocaleString()} UK undergraduates.`}
    >
      <ShieldCheck className={"h-3 w-3 mt-0.5 shrink-0 " + (isVerified ? "text-emerald-700" : "text-stone-500")} />
      <span>
        {isVerified ? (
          <>
            <span className="inline-block px-1.5 py-0.5 mr-1 rounded bg-emerald-600 text-white text-[9px] uppercase tracking-wider">
              HESA verified
            </span>
            NSS 2025: <span className="text-[#002147] font-semibold">{uniPct}%</span>
            {nResponses ? <span className="text-stone-500"> · n={nResponses.toLocaleString()}</span> : null}
            {" · vs UK sector "}
            <span className="text-stone-700">{nss.positivity_pct}%</span>{" "}
            <span className={delta >= 0 ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}>
              ({deltaSign}{delta} pts)
            </span>
          </>
        ) : (
          <>
            HESA NSS 2024 UK-sector benchmark: <span className="text-[#002147] font-semibold">{nss.positivity_pct}%</span>
            {" · "}
            <span className={delta >= 0 ? "text-emerald-700" : "text-red-700"}>
              this uni indicative equivalent {uniPct}% ({deltaSign}{delta} pts)
            </span>
          </>
        )}
      </span>
    </div>
  );
}
